/**
 * Le repos en cours, vu de n'importe quelle page élève.
 *
 * Le repos d'une séance vit dans localStorage (`workout_rest_<séance>`, écrit
 * par useSessionRest). La capsule flottante (FloatingRestTimer) le relit d'ici
 * pour rester visible quand l'élève quitte l'écran de séance — vidéothèque,
 * messages, planning — et pour faire sonner l'alarme là où il se trouve.
 *
 * Toucher la capsule ramène à l'exercice du repos : si l'écran de séance est
 * monté, il reçoit l'événement et ouvre l'exercice ; sinon la demande est
 * gardée ici le temps que le tableau de bord rouvre la séance.
 */
import { safeGetItem } from '../utils/storage';

export const REST_KEY_PREFIX = 'workout_rest_';
export const REST_CHANGE_EVENT = 'kaiylo:session-rest-change';
export const OPEN_REST_EXERCISE_EVENT = 'kaiylo:open-rest-exercise';

/** Fin du repos, en ms : la consigne du coach plus les « +30 s » demandés. */
export const restEndsAt = (rest) =>
  rest ? rest.startedAt + ((rest.seconds || 0) + (rest.extraSeconds || 0)) * 1000 : 0;

const listRestKeys = () => {
  const keys = [];
  try {
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const key = window.localStorage.key(i);
      if (key && key.startsWith(REST_KEY_PREFIX)) keys.push(key);
    }
  } catch {
    // Stockage inaccessible (navigation privée…) : pas de capsule.
  }
  return keys;
};

const parseRest = (raw) => {
  try {
    const rest = JSON.parse(raw || 'null');
    return rest && Number.isFinite(rest.startedAt) ? rest : null;
  } catch {
    return null;
  }
};

/** Le repos stocké pour une séance, fini ou non. */
export const readRestOf = (sessionId) =>
  (sessionId ? parseRest(safeGetItem(`${REST_KEY_PREFIX}${sessionId}`)) : null);

/** Le repos qui court en ce moment ({ sessionId, rest, endsAt }), le plus récent s'il y en a plusieurs. */
export const readActiveRest = (now = Date.now()) => {
  let best = null;
  for (const key of listRestKeys()) {
    const rest = parseRest(safeGetItem(key));
    if (!rest) continue;
    const endsAt = restEndsAt(rest);
    if (endsAt <= now) continue;
    if (!best || rest.startedAt > best.rest.startedAt) {
      best = { sessionId: key.slice(REST_KEY_PREFIX.length), rest, endsAt };
    }
  }
  return best;
};

/** Prévient la capsule qu'un repos vient d'être posé, prolongé ou retiré. */
export const notifyRestChange = (sessionId) => {
  try {
    window.dispatchEvent(new CustomEvent(REST_CHANGE_EVENT, { detail: { sessionId } }));
  } catch {
    // Pas d'événement possible : la capsule relit de toute façon à chaque tic.
  }
};

let pendingOpen = null;

/**
 * Ramener l'élève à l'exercice du repos. Renvoie true si l'écran de séance
 * (monté) s'en est chargé ; sinon la demande attend que la séance rouvre.
 */
export const requestOpenRestExercise = (sessionId, exerciseIndex) => {
  const detail = { sessionId, exerciseIndex, handled: false };
  window.dispatchEvent(new CustomEvent(OPEN_REST_EXERCISE_EVENT, { detail }));
  if (!detail.handled) pendingOpen = { sessionId, exerciseIndex };
  return detail.handled;
};

/** L'exercice à rouvrir pour cette séance, une seule fois. */
export const consumePendingRestExercise = (sessionId) => {
  if (!pendingOpen || String(pendingOpen.sessionId) !== String(sessionId)) return null;
  const { exerciseIndex } = pendingOpen;
  pendingOpen = null;
  return exerciseIndex;
};

/** Une séance a-t-elle une demande de réouverture en attente ? */
export const pendingRestSessionId = () => pendingOpen?.sessionId ?? null;
